import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CryptoJS from "crypto-js";
import http from "../../../utils/http";
import ApiConstants from "../../../constants/apiConstants";
import { AUTH_ROUTES } from "../../../constants/authRoutes";
import { buildAuthPath, getAuthContextFromSearch } from "../../../utils/authUtils";
import { LimeTrayLogo } from "../../../assets";
import appConstants from "../../../constants/appConstants";
import GoogleLoginButton from "./GoogleLoginButton";

const LIMETRAY_SSO_ERROR_MESSAGE =
  "Please use SSO.  Password based login is deactivated for limetray users.";

export default function SignInForm(props) {
  const initialUserState = {
    username: "",
    password: "",
    errors: "",
    otp: "",
  };

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [user, setUser] = useState(initialUserState);
  const [isOtpFieldVisible, setIsOtpFieldVisible] = useState(false);
  let { username, password, errors, otp } = user;

  const qs = getQueryVariable();
  let errorMessage = qs.get("error");
  let redirectUri = qs.get("redirect");

  useEffect(() => {
    if (errorMessage && errorMessage !== "") {
      try {
        errorMessage = atob(errorMessage);
      } catch (error) {
        errorMessage = error;
      }
      setUser((user) => ({ ...user, errors: errorMessage }));
    }
    if (redirectUri) {
      setUser((user) => ({ ...user, redirectUri: redirectUri }));
    }
  }, [errorMessage, redirectUri]);

  function getQueryVariable() {
    const map = new Map();
    const query = window.location.search.substring(1);
    const vars = query.split("&");
    for (let i = 0; i < vars.length; i++) {
      var pair = vars[i].split("=");
      map.set(pair[0], pair[1]);
    }
    return map;
  }

  const validateFields = () => {
    const {
      ifUserNameEmpty,
      ifPasswordEmpty,
      ifUserNameHasEnoughChar,
      ifPasswordHasEnoughChar,
      ifOtpEmpty,
      ifOtpHasEnoughChar,
    } = appConstants.validationMessage;
    if (!username.length) {
      return { isValid: false, message: ifUserNameEmpty };
    }
    if (!password.length) {
      return { isValid: false, message: ifPasswordEmpty };
    }
    if (isOtpFieldVisible && !otp.length) {
      return { isValid: false, message: ifOtpEmpty };
    }
    if (username.length < 4) {
      return { isValid: false, message: ifUserNameHasEnoughChar };
    }
    if (password.length < 4) {
      return { isValid: false, message: ifPasswordHasEnoughChar };
    }
    if (isOtpFieldVisible && otp.length !== 6) {
      return { isValid: false, message: ifOtpHasEnoughChar };
    }
    return { isValid: true, message: "" };
  };

  function getChecksum(username, password) {
    var hash = CryptoJS.SHA256(password);
    hash = CryptoJS.HmacSHA256(
      username.toString().toLowerCase(),
      hash.toString().toLowerCase()
    );
    return hash.toString(CryptoJS.enc.Hex);
  }

  function isLimeTrayUser(username) {
    return username.trim().toLowerCase().endsWith("@limetray.com");
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUser((user) => ({ ...user, [name]: value }));
  };

  const renderOTPField = () => (
    <div className="flex-column margin-top-20">
      <label className="font-16 font-bolder">* OTP</label>
      <input
        onChange={handleChange}
        type="number"
        placeholder="Enter OTP"
        name="otp"
        value={otp}
      />
    </div>
  );

  const checkIsMFAActivated = async (username) => {
    return await http.post(ApiConstants.CHECK_IS_MFA_ACTIVATED.URL, {}, {
      username,
    });
  };

  const sendMfaSecret = async () => {
    return await http.post(ApiConstants.SEND_MFA_SECRET.URL, {}, {});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username.length) {
      setUser((user) => ({
        ...user,
        errors: appConstants.validationMessage.ifUserNameEmpty,
      }));
      return;
    }
    if (isLimeTrayUser(username)) {
      setUser((user) => ({ ...user, errors: LIMETRAY_SSO_ERROR_MESSAGE }));
      return;
    }
    const { isValid, message } = validateFields();
    if (!isValid) {
      setUser((user) => ({ ...user, errors: message }));
      return event.preventDefault();
    }
    try {
      let result = false;
      if (!isOtpFieldVisible) {
        let res = await checkIsMFAActivated(username);
        result = res.result;
      }
      if (result && !isOtpFieldVisible) {
        setIsOtpFieldVisible(true);
        setUser((user) => ({ ...user, errors: "" }));
        return;
      }
      let passwordField = document.getElementById("password");
      passwordField.setAttribute("type", "password");
      passwordField.setAttribute("name", "userChecksum");
      passwordField.value = getChecksum(username, password);
      event.target.submit();
    } catch (response) {
      let message = "Some Error Occured!";
      if (response.response && response.response.status === 404) {
        message = response.response.data;
      }
      setUser((user) => ({ ...user, errors: message }));
    }
  };

  // ✅ Google Login Success Handler
    const handleGoogleLoginSuccess = async (googleResponse) => {
        const idToken = googleResponse?.credential;

        if (!idToken) {
            return setUser((user) => ({
                ...user,
                errors: "Failed to retrieve Google ID Token",
            }));
        }

        const brandId = qs.get("brandId") || "";
        const finalRedirectUri =
            qs.get("redirect") || `${window.location.origin}/dashboards`;

        try {
            const response = await fetch("/api/v1/oauth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    provider: "google",
                    token: idToken,
                    brandId,
                    productIdentifier: "login-page",
                    redirect: finalRedirectUri,
                }),
            });

            if (response.redirected) {
                window.location.href = response.url;
                return;
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "OAuth login failed");
            }
        } catch (error) {
            console.error(error);
            setUser((user) => ({
                ...user,
                errors: error.message || "Something went wrong during Google login",
            }));
        }
    };


    const { title, subTitlePrimary, subTitleSecondary, orContinueWith, loginButtonText, forgotPasswordLink } =
    appConstants.loginPageMessages;

  return (
    <div className="sign-in-form">
      <div className="sign-in-form-header">
        <div className="brand">
          <img className="limetrayLogo" alt="LimeTray" src={LimeTrayLogo} />
        </div>
        <h1 className="sm-font-35 margin-top-24">{title}</h1>
        <p className="font-16 margin-top-24">{subTitlePrimary}</p>
        <p className="font-16 margin-top-20">{subTitleSecondary}</p>
      </div>

      <form
        className="sign-in-form-main"
        onSubmit={handleSubmit}
        method="post"
        action="/api/v1/login"
      >
        <div className="sign-in-form-fields">
          <fieldset>
            <div className="flex-column margin-top-20">
              <label className="font-16 font-bolder">* Username</label>
              <input
                onChange={handleChange}
                type="text"
                placeholder="Enter your username"
                name="username"
                value={username}
              />
            </div>
            <div className="flex-column margin-top-20 relative">
              <label className="font-16 font-bolder">* Password</label>
              <input
                onChange={handleChange}
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Enter your password"
                name="password"
                value={password}
                id="password"
              />
              <input
                className="show-button cursor-pr"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                type="button"
                value={isPasswordVisible ? "Hide" : "Show"}
              />
            </div>

            <div className="auth-row-line margin-top-20">
              <div className="errors">{errors}</div>
              <Link
                className="auth-forgot-link"
                to={buildAuthPath(
                  AUTH_ROUTES.FORGOT_PASSWORD,
                  getAuthContextFromSearch(window.location.search),
                )}
              >
                {forgotPasswordLink}
              </Link>
            </div>

            {isOtpFieldVisible && renderOTPField()}

            <input
              id="redirect"
              name="redirect"
              type="hidden"
              value={redirectUri}
              spellCheck="false"
            />
          </fieldset>

          <fieldset className="login-button-fieldset">
            <button
              className="action-btn brd-radius font-16 cursor-pr"
              type="submit"
            >
              {loginButtonText}
            </button>
          </fieldset>

          <div className="social-login-section">
            <div className="social-divider font-16">
              <span className="social-divider-line" />
              <span className="social-divider-text">{orContinueWith}</span>
              <span className="social-divider-line" />
            </div>

            <div className="social-buttons">
              <div className="google-login-wrapper">
                <GoogleLoginButton
                  onSuccess={handleGoogleLoginSuccess}
                  onError={() =>
                    setUser((user) => ({
                      ...user,
                      errors: "Google Login Failed",
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="sign-in-form-footer">
        <p className="font-16 margin-top-24 sm-text-center">
          <span>Don't have an account ? No worries, </span>
          <a className="email-link" href="mailto:contactus@limetray.com">
            contactus@limetray.com
          </a>
          <span> to get started</span>
        </p>
      </div>
    </div>
  );
}